import React from 'react';
import { FixedSizeGrid } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import FileIcon from './FileIcon';
import { DEFAULT_DENSITY, columnWidth, densityOf, densityVars, rowHeight } from '../config/gridDensity';

// L-14: only virtualize for large workspaces. Below this threshold the common
// case renders byte-for-byte identical markup (risk-free).
const VIRTUALIZE_THRESHOLD = 150;

const renderIcons = (files, selection, onFileClick, onCheckboxClick, onContextSelect) =>
    files.map(file => (
        <FileIcon
            key={file.id}
            file={file}
            isSelected={selection.includes(file.id)}
            onFileClick={onFileClick}
            onCheckboxClick={onCheckboxClick}
            onContextSelect={onContextSelect}
        />
    ));

const FileGridView = ({
    files,
    selection,
    density = DEFAULT_DENSITY,
    onFileClick,
    onCheckboxClick,
    onContextSelect,
}) => {
    // Both paths below size themselves from this one object: the CSS grid
    // through custom properties, the windowed grid through pixel metrics.
    const metrics = densityOf(density);
    const cssVars = densityVars(metrics);

    // Common case: keep the exact existing markup unchanged.
    if (files.length <= VIRTUALIZE_THRESHOLD) {
        return (
            <div className="file-grid-view" style={cssVars}>
                {renderIcons(files, selection, onFileClick, onCheckboxClick, onContextSelect)}
            </div>
        );
    }

    return (
        <div className="file-grid-view" style={{ ...cssVars, display: 'block', height: '100%' }}>
            <AutoSizer>
                {({ height, width }) => {
                    // Unconstrained parent: fall back so the view never blanks.
                    if (height < 1 || width < 1) {
                        return (
                            <div className="file-grid-view">
                                {renderIcons(files, selection, onFileClick, onCheckboxClick, onContextSelect)}
                            </div>
                        );
                    }

                    const columnCount = Math.max(1, Math.floor(width / columnWidth(metrics)));
                    const rowCount = Math.ceil(files.length / columnCount);

                    const Cell = ({ columnIndex, rowIndex, style }) => {
                        const index = rowIndex * columnCount + columnIndex;
                        const file = files[index];
                        if (!file) return null;
                        // Pad inside the cell so cards keep the grid gap and the
                        // .file-card fills the remaining width.
                        return (
                            <div style={{ ...style, padding: `0 ${metrics.gap}px ${metrics.gap}px 0`, boxSizing: 'border-box' }}>
                                <FileIcon
                                    file={file}
                                    isSelected={selection.includes(file.id)}
                                    onFileClick={onFileClick}
                                    onCheckboxClick={onCheckboxClick}
                                    onContextSelect={onContextSelect}
                                />
                            </div>
                        );
                    };

                    return (
                        <FixedSizeGrid
                            columnCount={columnCount}
                            columnWidth={columnWidth(metrics)}
                            rowCount={rowCount}
                            rowHeight={rowHeight(metrics)}
                            height={height}
                            width={width}
                        >
                            {Cell}
                        </FixedSizeGrid>
                    );
                }}
            </AutoSizer>
        </div>
    );
};

export default FileGridView;
